import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { RecordProducerContext } from "./RecordProducerContext";
import { useRecordProducerQuery } from "modules/record/record-producer/hooks/useRecordProducerQuery";
import { useFormLayout } from "../hooks/useFormLayout";
import { useFormView } from "../hooks/useFormView";
import { buildFormLayout, groupFormSections, FormLayoutData } from "../services/formLayoutBuilder";
import { RecordFormSection } from "./RecordFormSection";
import { RecordFormSectionTabs } from "./RecordFormSectionTabs";
import { FormViewSelector } from "./FormViewSelector";
import { FieldFactory } from "./FieldFactory";
import RecordProducerSkeletonComponent from "./record-producer-views/RecordProducerSkeleton";
import RecordProducerNoTableFoundComponent from "./record-producer-views/RecordProducerNoTableFound";
import { Box, Link, Paper } from "@mui/material";
import { RecordProducerActionsComponent } from "./RecordProducerActions";
import RecordRelatedListComponent from "modules/record/record-related-list/components/RecordRelatedList";
import { automaticFormColumns } from "../services/automaticFormColumns";
import { usePageTitle } from "app/hooks/usePageTitle";
import { useTranslationsReady } from "app/providers/TranslationProvider";
import { useTranslation } from "react-i18next";
import RecordProducerNoAccessComponent from "./record-producer-views/RecordProducerNoAccess";
import RecordModuleErrorState from "modules/record/shared/components/RecordModuleErrorState";
import {
    deserializeRecordProducerPrefill,
    getRecordProducerPrefillParamKey,
} from "../services/recordProducerPrefill";
import { useUiRules } from "../hooks/useUiRules";
import { evaluateUiRules } from "../services/uiRuleEvaluator";
import { useSession } from "app/hooks/useSession";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { AttachmentPanel } from "modules/record/shared/attachments/AttachmentPanel";
import useClientScripts from "../hooks/useClientScripts";
import {
    buildClientScriptBaseContext,
    ClientScriptEvent,
    ClientScriptEventType,
    executeClientScript,
} from "../services/clientScriptRuntime";
import {
    applyDefaultValueScripts,
    buildDefaultValueScriptBaseContext,
    buildLiteralDefaultValues,
} from "../services/defaultValueScriptRuntime";
import { FieldRuntimeProps } from "../types/record-producer-types";
import useNotification from "component/global/event-handler/hooks/use-notification";
import { AclFieldPermissions, useAclFieldPermissions } from "../hooks/useAclFieldPermissions";
import { isPermissionError } from "modules/record/shared/services/recordQaBehavior";
import {
    isSameValueDispatchNeeded,
    selectOnChangeScriptsForField,
} from "../services/clientScriptOnChangeDispatch";

type Props = {
    tableName: string;
    sysId?: string;
    previewLayout?: FormLayoutData;
    previewViewId?: string;
    // Fluss task page (Onda 11.1 E7): the no-access panel explains which table access is missing.
    explainTaskAccess?: boolean;
    embedded?: boolean;
    showFullRecordLink?: boolean;
    readOnly?: boolean;
    onFormStateChange?: (state: RecordProducerFormState | null) => void;
};

export type RecordProducerFormState = {
    isDirty: boolean;
    save: () => Promise<boolean>;
    discard: () => void;
};

const RECORD_PRODUCER_DEBUG_STORAGE_KEY = "debug.recordProducer";

const isRecordProducerDebugEnabled = () => {
    if (typeof window === "undefined") {
        return false;
    }

    return window.localStorage.getItem(RECORD_PRODUCER_DEBUG_STORAGE_KEY) === "1";
};

const summarizeChangedFields = (
    previousValues: Record<string, any> | null,
    nextValues: Record<string, any>
) => {
    if (!previousValues) {
        return Object.keys(nextValues);
    }

    return Object.keys(nextValues).filter((key) => {
        const previousValue = previousValues[key];
        const nextValue = nextValues[key];
        return JSON.stringify(previousValue) !== JSON.stringify(nextValue);
    });
};

const createAttachmentDraftToken = () => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID().replace(/-/g, "");
    }

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 18)}`;
};

const mergeFieldRuntimeStates = (
    baseStates: Record<string, FieldRuntimeProps>,
    overrideStates: Record<string, FieldRuntimeProps>
) => {
    const merged: Record<string, FieldRuntimeProps> = { ...baseStates };

    Object.entries(overrideStates).forEach(([fieldName, state]) => {
        merged[fieldName] = {
            ...(merged[fieldName] || {}),
            ...state,
        };
    });

    return merged;
};


const applyAclFieldRuntimeStates = (
    currentStates: Record<string, FieldRuntimeProps>,
    fieldPermissions: AclFieldPermissions
) => {
    const nextStates: Record<string, FieldRuntimeProps> = { ...currentStates };

    Object.entries(fieldPermissions).forEach(([fieldName, permission]) => {
        const current = nextStates[fieldName] || {};

        if (permission === "hidden") {
            nextStates[fieldName] = {
                ...current,
                hidden: true,
                required: false,
            };
            return;
        }

        if (permission === "readonly" && !current.hidden) {
            nextStates[fieldName] = {
                ...current,
                readonly: true,
            };
        }
    });

    return nextStates;
};

const titleValueFor = (value: unknown) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        const record = value as Record<string, unknown>;
        return String(record.display ?? record.label ?? record.name ?? record.number_ref ?? record.id ?? "");
    }

    return String(value);
};

function RecordProducerInner({ tableName, sysId, explainTaskAccess, previewLayout, previewViewId, embedded = false, showFullRecordLink = true, readOnly = false, onFormStateChange }: Props) {
    const { t, i18n } = useTranslation();
    const translationsReady = useTranslationsReady();
    const navigate = useNavigate();
    const notifyApi = useNotification();
    const [searchParams] = useSearchParams();
    const formViewParam = searchParams.get("form_view");
    const prefillParam = searchParams.get(getRecordProducerPrefillParamKey());
    const session = useSession();

    const {
        table: currentTable,
        columns,
        record,
        isLoading,
        isError,
        error,
        refetch,
        dataUpdatedAt,
    } = useRecordProducerQuery(tableName, sysId);
    const mode = sysId ? "edit" : "create";
    const {
        fieldPermissions,
        permissions,
        isLoading: fieldPermissionsLoading,
        isError: fieldPermissionsIsError,
        error: fieldPermissionsError,
    } = useAclFieldPermissions({ tableName, mode, record });
    const canLoadDependencies = previewLayout ? true : mode === "edit"
        ? Boolean(record) && !isPermissionError(error)
        : !fieldPermissionsLoading && !fieldPermissionsIsError && permissions?.create !== false;
    const { views, viewId, defaultViewId, selectView, isLoading: viewsLoading } = useFormView(currentTable?.id, formViewParam, currentTable?.name, canLoadDependencies);
    const { layout, isLoading: layoutLoading } = useFormLayout(currentTable?.id, viewId, canLoadDependencies);
    const { data: uiRules = [], isLoading: uiRulesLoading } = useUiRules(currentTable?.id, canLoadDependencies);
    const prefillValues = useMemo(
        () => deserializeRecordProducerPrefill(prefillParam),
        [prefillParam]
    );

    const defaultValues = useMemo(
        () => ({
            ...buildLiteralDefaultValues(columns ?? []),
            ...prefillValues,
        }),
        [columns, prefillValues]
    );

    const form = useForm<Record<string, any>>({
        mode: "onChange",
        defaultValues
    });

    const attachmentDraftTokenRef = useRef<string>(createAttachmentDraftToken());
    const hydratedVersionRef = useRef<string | null>(null);
    const hydratedCreateDefaultsRef = useRef<string | null>(null);
    const pendingTranslationFlushesRef = useRef<Map<string, () => Promise<boolean>>>(new Map());
    const registerPendingTranslation = useCallback((fieldName: string, flush: () => Promise<boolean>) => {
        pendingTranslationFlushesRef.current.set(fieldName, flush);
        return () => {
            if (pendingTranslationFlushesRef.current.get(fieldName) === flush) {
                pendingTranslationFlushesRef.current.delete(fieldName);
            }
        };
    }, []);
    const flushPendingTranslations = useCallback(async () => {
        const results: boolean[] = [];
        for (const flush of pendingTranslationFlushesRef.current.values()) {
            results.push(await flush());
        }
        return results.every(Boolean);
    }, []);
    const previousValuesRef = useRef<Record<string, any> | null>(null);
    const clientScriptPreviousValuesRef = useRef<Record<string, any> | null>(null);
    const skipHydrationChangesRef = useRef(false);
    const onLoadExecutionKeyRef = useRef<string | null>(null);
    const renderCountRef = useRef(0);
    const [clientScriptFieldStates, setClientScriptFieldStates] = useState<Record<string, FieldRuntimeProps>>({});
    const [createDefaultsReady, setCreateDefaultsReady] = useState(mode !== "create");
    const recordProducerDebugEnabled = isRecordProducerDebugEnabled();
    const defaultValueScriptBaseContext = useMemo(
        () => buildDefaultValueScriptBaseContext({
            notifyApi,
            navigate,
            userSession: session.data,
        }),
        [navigate, notifyApi, session.data]
    );

    const recordVersion = record ? `${record.data?.id ?? sysId}:${record.data?.updated_at ?? dataUpdatedAt}` : null;
    const isDirty = form.formState.isDirty;
    const saveRef = useRef<(() => Promise<boolean>) | null>(null);
    const registerSave = useCallback((save: (() => Promise<boolean>) | null) => { saveRef.current = save; }, []);
    useEffect(() => {
        if (!onFormStateChange) return;
        onFormStateChange({
            isDirty,
            save: () => saveRef.current?.() ?? Promise.resolve(false),
            discard: () => form.reset(record?.data ?? defaultValues),
        });
        return () => onFormStateChange(null);
    }, [onFormStateChange, isDirty, form, record, defaultValues]);

    useEffect(() => {
        if (mode !== "edit" || isLoading || !record || recordVersion === hydratedVersionRef.current) {
            return;
        }

        if (hydratedVersionRef.current !== null && isDirty) {
            return;
        }

        form.reset(record.data);
        hydratedVersionRef.current = recordVersion;
        clientScriptPreviousValuesRef.current = { ...form.getValues() };
        skipHydrationChangesRef.current = true;
    }, [isLoading, record, recordVersion, mode, form, isDirty]);

    useEffect(() => {
        if (mode !== "create" || isLoading) {
            return;
        }

        const defaultScriptSignature = JSON.stringify((columns ?? []).map((column) => ({
            id: column.id,
            type: column.default_value_type,
            script: column.default_value_script,
            timeout: column.default_value_timeout_ms,
        })));
        const serializedDefaults = JSON.stringify({ defaultValues, defaultScriptSignature });
        if (hydratedCreateDefaultsRef.current === serializedDefaults) {
            return;
        }

        let cancelled = false;
        setCreateDefaultsReady(false);

        const resetDefaults = async () => {
            try {
                const resolvedDefaults = currentTable
                    ? await applyDefaultValueScripts({
                        columns: columns ?? [],
                        baseValues: buildLiteralDefaultValues(columns ?? []),
                        baseContext: defaultValueScriptBaseContext,
                        table: currentTable as unknown as Record<string, unknown>,
                        tableName,
                    })
                    : buildLiteralDefaultValues(columns ?? []);

                if (cancelled) {
                    return;
                }

                form.reset({
                    ...resolvedDefaults,
                    ...prefillValues,
                });
                hydratedCreateDefaultsRef.current = serializedDefaults;
                setCreateDefaultsReady(true);
            } catch (error: any) {
                if (!cancelled) {
                    notifyApi.alert(
                        "DEFAULT_VALUE_SCRIPT_ERROR",
                        error?.message || "Error executing default value script"
                    );
                    form.reset(defaultValues);
                    hydratedCreateDefaultsRef.current = serializedDefaults;
                    setCreateDefaultsReady(true);
                }
            }
        };

        resetDefaults();

        return () => {
            cancelled = true;
        };
    }, [
        columns,
        currentTable,
        defaultValueScriptBaseContext,
        defaultValues,
        form,
        isLoading,
        mode,
        notifyApi,
        prefillValues,
        tableName,
    ]);


    const values = useWatch({ control: form.control });

    useEffect(() => {
        if (!recordProducerDebugEnabled) {
            return;
        }

        renderCountRef.current += 1;

        const nextValues = (values ?? {}) as Record<string, any>;
        const changedFields = summarizeChangedFields(previousValuesRef.current, nextValues);

        console.groupCollapsed(`[RecordProducerDebug] Render ${renderCountRef.current} for ${tableName}`);
        console.log({
            mode,
            sysId: sysId ?? null,
            currentTable: currentTable?.name ?? null,
            changedFields,
            valueCount: Object.keys(nextValues).length,
            uiRuleCount: uiRules.length,
        });
        console.groupEnd();

        previousValuesRef.current = nextValues;
    }, [
        currentTable?.name,
        mode,
        recordProducerDebugEnabled,
        sysId,
        tableName,
        uiRules.length,
        values,
    ]);

    const clientScriptConditionContext = useMemo(
        () => ({
            form: (values ?? {}) as Record<string, unknown>,
            currentTableName: tableName,
            isNewRecord: !sysId,
            user: { id: session.data?.id || "", roles: session.data?.roles || [] },
        }),
        [session.data?.id, session.data?.roles, sysId, tableName, values]
    );
    const { byEvent: clientScripts, isLoading: clientScriptsLoading } = useClientScripts(
        currentTable?.id,
        clientScriptConditionContext,
        canLoadDependencies
    );

    const uiRuleFieldRuntimeStates = useMemo(
        () => evaluateUiRules(columns ?? [], uiRules, {
            mode,
            tableName,
            values: values ?? {},
            user: session.data
                ? {
                    id: session.data.id,
                    email: session.data.email,
                    name: session.data.name,
                }
                : null,
        }),
        [columns, mode, session.data, tableName, uiRules, values]
    );

    const fieldRuntimeStates = useMemo(
        () => {
            const states = applyAclFieldRuntimeStates(
                mergeFieldRuntimeStates(uiRuleFieldRuntimeStates, clientScriptFieldStates),
                fieldPermissions
            );
            return columns?.some(column => column.name === "sys_class_name")
                ? { ...states, sys_class_name: { ...states.sys_class_name, readonly: true } }
                : states;
        },
        [clientScriptFieldStates, columns, fieldPermissions, uiRuleFieldRuntimeStates]
    );
    const displayedFieldStates = useMemo(() => (previewLayout || readOnly)
        ? Object.fromEntries((columns ?? []).map(column => [column.name, { ...fieldRuntimeStates[column.name], readonly: true }]))
        : fieldRuntimeStates, [previewLayout, readOnly, columns, fieldRuntimeStates]);
    const formSections = useMemo(
        () => {
            const selectedId = previewViewId ?? viewId;
            const selected = views.find(view => view.id === selectedId);
            const childLabel = String(t("form_layout.section.child_fields", { defaultValue: "Campos de {{table}}", table: currentTable?.label ? t(currentTable.label) : currentTable?.name ?? "" }));
            const childColumnIds = selected?.inherited_from_table
                ? (columns ?? []).filter(column => column.source_sys_table_id === currentTable?.id).map(column => column.id)
                : [];
            const inheritedFields = selected?.inherited_from_table
                ? { childColumnIds, childLabel }
                : undefined;
            const sourceLayout = previewLayout ?? layout;
            const resolvedLayout = sourceLayout && {
                ...sourceLayout,
                sections: sourceLayout.sections.map(section => (section.label_key ?? section.label) === "form_layout.section.child_fields"
                    ? { ...section, label: childLabel } : section),
            };
            return buildFormLayout(columns ?? [], resolvedLayout, fieldRuntimeStates, selectedId, defaultViewId, inheritedFields);
        },
        [columns, layout, previewLayout, previewViewId, fieldRuntimeStates, viewId, defaultViewId, views, currentTable, t]
    );
    const currentTableLabel = currentTable?.label ? String(t(currentTable.label)) : currentTable?.name ?? "";
    const formBlocks = useMemo(() => formSections && groupFormSections(formSections), [formSections]);

    const setClientScriptFieldRuntimeState = useCallback((
        fieldName: string,
        patch: FieldRuntimeProps | ((previous: FieldRuntimeProps) => FieldRuntimeProps)
    ) => {
        setClientScriptFieldStates((previous) => {
            const current = previous[fieldName] || {};
            const next = typeof patch === "function" ? patch(current) : { ...current, ...patch };

            return {
                ...previous,
                [fieldName]: next,
            };
        });
    }, []);

    const clientScriptBaseContext = useMemo(
        () => buildClientScriptBaseContext({
            notifyApi,
            navigate,
            userSession: session.data,
        }),
        [navigate, notifyApi, session.data]
    );

    const runClientScriptList = useCallback(async (
        scripts: any[],
        event: ClientScriptEvent,
        previousOverride?: Record<string, unknown> | null
    ) => {
        if (!currentTable || !scripts.length) {
            return true;
        }

        const current = form.getValues();
        const previous = previousOverride ?? clientScriptPreviousValuesRef.current ?? {};

        for (const script of scripts) {
            try {
                const result = await executeClientScript({
                    script,
                    event,
                    form,
                    baseContext: clientScriptBaseContext,
                    current,
                    previous,
                    table: currentTable as unknown as Record<string, unknown>,
                    tableName,
                    columns,
                    currentLanguage: i18n.resolvedLanguage || i18n.language || "pt-BR",
                    translateMessage: (message) => String(t(message)),
                    setFieldRuntimeState: setClientScriptFieldRuntimeState,
                });

                if (result.blocked) {
                    return false;
                }
            } catch (error: any) {
                notifyApi.alert(
                    "CLIENT_SCRIPT_RUNTIME_ERROR",
                    error?.message || "Error executing client script"
                );
                console.error("ClientScriptRuntimeError:", error);
                return false;
            }
        }

        return true;
    }, [
        clientScriptBaseContext,
        columns,
        currentTable,
        form,
        i18n,
        notifyApi,
        setClientScriptFieldRuntimeState,
        tableName,
        t,
    ]);

    const runClientScripts = useCallback(async (eventType: ClientScriptEventType) => {
        const scripts = clientScripts[eventType] || [];
        return runClientScriptList(scripts, { type: eventType });
    }, [clientScripts, runClientScriptList]);

    const dispatchSameValueFieldChange = useCallback((fieldName: string) => {
        if (isLoading || clientScriptsLoading || !currentTable || (mode === "create" && !createDefaultsReady)) {
            return;
        }

        const previousValues = clientScriptPreviousValuesRef.current;
        const currentValue = form.getValues(fieldName);
        if (!isSameValueDispatchNeeded(previousValues, currentValue, fieldName)) {
            return;
        }

        const scripts = selectOnChangeScriptsForField(clientScripts.onChange, columns, fieldName);
        runClientScriptList(
            scripts,
            { type: "onChange", field: fieldName, oldValue: currentValue, newValue: currentValue },
            previousValues
        );
    }, [
        clientScripts.onChange,
        clientScriptsLoading,
        columns,
        createDefaultsReady,
        currentTable,
        form,
        isLoading,
        mode,
        runClientScriptList,
    ]);

    useEffect(() => {
        if (isLoading || clientScriptsLoading || !currentTable || (mode === "create" && !createDefaultsReady)) {
            return;
        }

        const executionKey = `${mode}:${currentTable.id}:${sysId || "new"}:${sysId ? "record" : hydratedCreateDefaultsRef.current || ""}`;
        if (onLoadExecutionKeyRef.current === executionKey) {
            return;
        }

        onLoadExecutionKeyRef.current = executionKey;
        runClientScriptList(clientScripts.onLoad, { type: "onLoad" });
        clientScriptPreviousValuesRef.current = { ...form.getValues() };
    }, [
        clientScripts.onLoad,
        clientScriptsLoading,
        createDefaultsReady,
        currentTable,
        form,
        isLoading,
        mode,
        runClientScriptList,
        sysId,
    ]);

    useEffect(() => {
        if (isLoading || clientScriptsLoading || !currentTable || (mode === "create" && !createDefaultsReady)) {
            return;
        }

        if (skipHydrationChangesRef.current) {
            skipHydrationChangesRef.current = false;
            return;
        }

        const nextValues = (values ?? {}) as Record<string, any>;
        const previousValues = clientScriptPreviousValuesRef.current;

        if (!previousValues) {
            clientScriptPreviousValuesRef.current = { ...nextValues };
            return;
        }

        const changedFields = summarizeChangedFields(previousValues, nextValues);
        clientScriptPreviousValuesRef.current = { ...nextValues };

        changedFields.forEach((fieldName) => {
            const scripts = selectOnChangeScriptsForField(clientScripts.onChange, columns, fieldName);

            runClientScriptList(
                scripts,
                {
                    type: "onChange",
                    field: fieldName,
                    oldValue: previousValues[fieldName],
                    newValue: nextValues[fieldName],
                },
                previousValues
            );
        });
    }, [
        clientScripts.onChange,
        clientScriptsLoading,
        columns,
        currentTable,
        createDefaultsReady,
        isLoading,
        mode,
        runClientScriptList,
        values,
    ]);

    useEffect(() => {
        Object.entries(fieldRuntimeStates).forEach(([fieldName, state]) => {
            if (!state.hidden || !state.clearValueWhenHidden) {
                return;
            }

            const currentValue = values?.[fieldName];
            if (currentValue === null || currentValue === undefined || currentValue === "") {
                return;
            }

            form.clearErrors(fieldName);
            form.setValue(fieldName, null, {
                shouldDirty: true,
                shouldTouch: false,
                shouldValidate: false,
            });
        });
    }, [fieldRuntimeStates, form, values]);

    useEffect(() => {
        const hiddenFields = Object.entries(fieldRuntimeStates)
            .filter(([, state]) => state.hidden)
            .map(([fieldName]) => fieldName);

        if (hiddenFields.length > 0) {
            form.clearErrors(hiddenFields);
        }
    }, [fieldRuntimeStates, form]);

    const pageTitle = useMemo(() => {
        if (!currentTable) {
            return undefined;
        }

        const displayField = columns.find(c => c.is_display);
        const tableLabel = translateMetadataLabel(t, currentTable);

        if (mode === "create") {
            return `${t("Record for")} ${tableLabel}`;
        }

        const recordData = record?.data ?? {};
        const displayValue = titleValueFor(recordData[displayField?.name || "number_ref"]) || sysId || "";

        return displayValue ? `${tableLabel}: ${displayValue}` : tableLabel;
    }, [columns, currentTable, mode, record, sysId, t]);
    usePageTitle(pageTitle, {
        id: `record-producer:${tableName}:${sysId || "new"}`,
        enabled: !embedded && translationsReady && Boolean(pageTitle) && (mode === "create" || Boolean(record)),
        priority: 100,
    });

    if (!currentTable) {
        if (isLoading) return <RecordProducerSkeletonComponent columnList={columns} />;
        return <RecordProducerNoTableFoundComponent />;
    }

    if (isPermissionError(error) || isPermissionError(fieldPermissionsError) || (!previewLayout && mode === "create" && permissions?.create === false)) {
        return <RecordProducerNoAccessComponent tableName={tableName}
            taskTableLabel={explainTaskAccess ? translateMetadataLabel(t, currentTable) : undefined} />;
    }

    if (isLoading || layoutLoading || viewsLoading || uiRulesLoading || clientScriptsLoading || fieldPermissionsLoading || (mode === "create" && !createDefaultsReady)) {
        return <RecordProducerSkeletonComponent columnList={columns} />;
    }

    if (isError || fieldPermissionsIsError) {
        return (
            <RecordModuleErrorState
                title={String(t("We couldn't load this record"))}
                description={String(t("This record is temporarily unavailable. You can try again without leaving the page."))}
                error={error || fieldPermissionsError}
                onRetry={() => { void refetch(); }}
            />
        );
    }

    return (
        <RecordProducerContext.Provider
            value={{
                table: currentTable,
                columns,
                form,
                mode,
                embedded,
                recordId: sysId ?? null,
                translationKeys: record?.data?._translation_keys ?? {},
                attachmentDraftToken: mode === "create" ? attachmentDraftTokenRef.current : null,
                runClientScripts,
                dispatchSameValueFieldChange,
                registerPendingTranslation,
                flushPendingTranslations,
            }}
        >
            <FormProvider {...form}>
                {embedded && showFullRecordLink && sysId && <Box sx={{ mb: 1 }}>
                    <Link component={RouterLink} to={`/home/workspace/${encodeURIComponent(tableName)}/record/${encodeURIComponent(sysId)}`}>
                        {t("fluss.task.open_full_record", { defaultValue: "Abrir registro completo" })}
                    </Link>
                </Box>}
                <Box data-testid={embedded ? "record-producer-embedded-actions" : undefined} sx={{ mb: { xs: 0, sm: 1 },
                    ...(embedded ? { position: "sticky", top: "var(--record-action-sticky-top, 0px)", zIndex: (theme) => theme.zIndex.appBar,
                        bgcolor: "background.paper" } : {}) }}>
                    {!previewLayout && !readOnly && <RecordProducerActionsComponent
                        tableName={tableName}
                        id={sysId}
                        permissions={permissions}
                        onSaveReady={onFormStateChange ? registerSave : undefined}
                        leadingContent={<FormViewSelector views={views} value={viewId} onChange={selectView} tableName={tableName} />}
                    />}
                </Box>
                <Paper
                    elevation={1}
                    sx={{
                        p: { xs: 1.25, sm: 2 },
                        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: 1.5, sm: 2 },
                        ...(embedded ? { containerType: "inline-size", containerName: "record-producer" } : {}),
                    }}
                >
                    {formBlocks ? formBlocks.map(block => (
                        block.type === "section"
                            ? <RecordFormSection key={block.section.id} section={block.section} runtimeStates={displayedFieldStates} tableLabel={currentTableLabel} embedded={embedded} />
                            : <RecordFormSectionTabs key={block.id} sections={block.sections} runtimeStates={displayedFieldStates} tableLabel={currentTableLabel} embedded={embedded} />
                    )) : automaticFormColumns(columns ?? []).map((column) => (
                        <FieldFactory
                            key={column.id}
                            column={column}
                            runtimeProps={displayedFieldStates[column.name]}
                        />
                    ))}
                </Paper>
                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                    {!previewLayout && !readOnly && <AttachmentPanel
                        tableName={tableName}
                        recordId={sysId ?? null}
                        draftToken={mode === "create" ? attachmentDraftTokenRef.current : null}
                        title={String(t("Record attachments"))}
                        collapsible
                    />}
                </Box>
                {sysId && !previewLayout && !embedded && <RecordRelatedListComponent record={record} />}
                <Box sx={{ display: { xs: "block", sm: "none" }, height: "calc(72px + env(safe-area-inset-bottom))" }} />
            </FormProvider>
        </RecordProducerContext.Provider>
    );
}

export default function RecordProducer(props: Props) {
    return <RecordProducerInner key={`${props.tableName}:${props.sysId ?? "new"}`} {...props} />;
}
